import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { Member } from '@stytch/vanilla-js'
import { Users, Plus } from 'lucide-react'

interface UsersCardsProps {
	members: Member[]
	onSelectUser: (member: Member) => void
	onAddUser: () => void
	selectedUserId?: string
}

export function UsersCards({ members, onSelectUser, onAddUser, selectedUserId }: UsersCardsProps) {
	if (members.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center text-center space-y-3 bg-[#3A2E2F]/5 h-32 py-8">
				<Users className="h-8 w-8 text-foreground/60" />
				<p className="text-sm font-medium text-muted-foreground">No users yet</p>
				<Button variant="outline" size="sm" onClick={onAddUser}>
					<Plus className="h-4 w-4 mr-2" />
					Add User
				</Button>
			</div>
		)
	}

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			{members.map((member) => (
				<Card
					key={member.member_id}
					className={`cursor-pointer hover:shadow-md transition-shadow ${
						selectedUserId === member.member_id ? 'ring-2 ring-primary' : ''
					}`}
					onClick={() => onSelectUser(member)}
				>
					<CardContent className="p-4">
						<div className="flex items-center gap-3">
							<Avatar className="h-10 w-10">
								<AvatarImage src="" alt={member.name || member.email_address} />
								<AvatarFallback>
									{(() => {
										const text = member.name || member.email_address
										const parts = text.split(' ')
										return parts.length > 1
											? parts.slice(0, 2).map((n) => n[0]).join('').toUpperCase()
											: text.slice(0, 2).toUpperCase()
									})()}
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 min-w-0">
								<div className="font-medium truncate">{member.name || 'Unnamed User'}</div>
								<div className="text-sm text-muted-foreground truncate">{member.email_address}</div>
								<div className="flex items-center gap-2 mt-2">
									<Badge variant="secondary" className="capitalize text-xs">
										{member.roles?.[0]?.role_id || 'member'}
									</Badge>
									<Badge variant={member.status === 'active' ? 'default' : 'secondary'} className="text-xs">
										{member.status}
									</Badge>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			))}
		</div>
	)
}
